import { SEO } from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Mail, MessageCircle, Clock, Send, MessageSquare } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { z } from "zod";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

// Validation schemas
const enquirySchema = z.object({
  name: z.string().trim().min(1, "Name is required").max(100, "Name must be less than 100 characters"),
  email: z.string().trim().email("Invalid email address").max(255, "Email must be less than 255 characters"),
  subject: z.string().trim().min(1, "Subject is required").max(200, "Subject must be less than 200 characters"),
  message: z.string().trim().min(10, "Message must be at least 10 characters").max(1000, "Message must be less than 1000 characters")
});

const feedbackSchema = z.object({
  name: z.string().trim().min(1, "Name is required").max(100, "Name must be less than 100 characters"),
  email: z.string().trim().email("Invalid email address").max(255, "Email must be less than 255 characters"),
  rating: z.number().int().min(1).max(5),
  category: z.string().trim().min(1, "Category is required"),
  feedback: z.string().trim().min(10, "Feedback must be at least 10 characters").max(1000, "Feedback must be less than 1000 characters")
});

const Contact = () => {
  const [enquiryData, setEnquiryData] = useState({
    name: "",
    email: "",
    subject: "",
    message: ""
  });

  const [feedbackData, setFeedbackData] = useState({
    name: "",
    email: "",
    rating: 0,
    category: "",
    feedback: ""
  });

  const whatsappNumber = "+15055816117";

  const schema = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "name": "Contact HiStage",
    "description": "Get in touch with the HiStage team. We're here to help with questions about streaming, partnerships, or support.",
    "url": "https://histage.lovable.app/contact"
  };

  const handleEnquirySubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const validation = enquirySchema.safeParse(enquiryData);
    if (!validation.success) {
      toast.error(validation.error.errors[0].message);
      return;
    }

    toast.success("Enquiry sent! We'll get back to you soon.");
    setEnquiryData({ name: "", email: "", subject: "", message: "" });
  };

  const handleFeedbackSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const validation = feedbackSchema.safeParse(feedbackData);
    if (!validation.success) {
      toast.error(validation.error.errors[0].message);
      return;
    }

    toast.success("Thank you for your feedback!");
    setFeedbackData({ name: "", email: "", rating: 0, category: "", feedback: "" });
  };

  const handleWhatsAppClick = () => {
    const message = "Hi, I need help with HiStage";
    const encodedMessage = encodeURIComponent(message);
    window.open(`https://wa.me/${whatsappNumber.replace(/\+/g, '')}?text=${encodedMessage}`, '_blank');
  };

  return (
    <>
      <SEO 
        title="Contact HiStage - Get in Touch"
        description="Have questions about HiStage? Contact our team for support, partnerships, or general inquiries. We're here to help creators and fans succeed on our platform."
        url="/contact"
        keywords={[
          "contact histage",
          "customer support",
          "streaming help",
          "business inquiries",
          "partnerships",
          "get in touch"
        ]}
        schema={schema}
      />
      
      <main className="min-h-screen bg-background">
        {/* Hero */}
        <section className="py-20 bg-gradient-to-b from-primary/5 to-background">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-5xl font-bold text-foreground mb-6">
                Get in Touch
              </h1>
              <p className="text-xl text-muted-foreground">
                We're here to help. Send us a message and we'll respond as soon as possible.
              </p>
            </div>
          </div>
        </section>

        {/* Contact Info & Forms */}
        <section className="py-20">
          <div className="container mx-auto px-6">
            <div className="max-w-6xl mx-auto">
              <div className="grid lg:grid-cols-3 gap-12">
                {/* Contact Info */}
                <div className="space-y-6">
                  <div className="clean-card rounded-2xl p-6">
                    <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                      <Mail className="h-6 w-6 text-primary" />
                    </div>
                    <h3 className="font-semibold text-foreground mb-2">Email Us</h3>
                    <a href="mailto:support@histage.com" className="text-sm text-primary hover:underline mb-2 block">
                      support@histage.com
                    </a>
                    <p className="text-xs text-muted-foreground">For general inquiries</p>
                  </div>

                  <div className="clean-card rounded-2xl p-6">
                    <div className="h-12 w-12 rounded-xl bg-green-500/10 flex items-center justify-center mb-4">
                      <MessageCircle className="h-6 w-6 text-green-500" />
                    </div>
                    <h3 className="font-semibold text-foreground mb-2">WhatsApp</h3>
                    <p className="text-sm text-muted-foreground mb-2">{whatsappNumber}</p>
                    <Button 
                      variant="outline" 
                      className="rounded-full mt-2 w-full"
                      onClick={handleWhatsAppClick}
                    >
                      Chat on WhatsApp
                    </Button>
                  </div>

                  <div className="clean-card rounded-2xl p-6">
                    <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                      <Clock className="h-6 w-6 text-primary" />
                    </div>
                    <h3 className="font-semibold text-foreground mb-2">Response Time</h3>
                    <p className="text-sm text-muted-foreground">Typically within 24 hours</p>
                  </div>
                </div>

                {/* Contact Forms */}
                <div className="lg:col-span-2">
                  <Card className="clean-card rounded-2xl">
                    <CardContent className="p-8">
                      <Tabs defaultValue="enquiry" className="w-full">
                        <TabsList className="grid w-full grid-cols-2 mb-6">
                          <TabsTrigger value="enquiry">
                            <Send className="h-4 w-4 mr-2" />
                            Enquiry
                          </TabsTrigger>
                          <TabsTrigger value="feedback">
                            <MessageSquare className="h-4 w-4 mr-2" />
                            Feedback
                          </TabsTrigger>
                        </TabsList>

                        <TabsContent value="enquiry">
                          <form onSubmit={handleEnquirySubmit} className="space-y-6">
                            <div>
                              <label htmlFor="enquiry-name" className="block text-sm font-medium text-foreground mb-2">
                                Name *
                              </label>
                              <input
                                type="text"
                                id="enquiry-name"
                                value={enquiryData.name}
                                onChange={(e) => setEnquiryData(prev => ({ ...prev, name: e.target.value }))}
                                required
                                maxLength={100}
                                className="w-full px-4 py-3 rounded-lg border border-input bg-background text-foreground"
                              />
                            </div>

                            <div>
                              <label htmlFor="enquiry-email" className="block text-sm font-medium text-foreground mb-2">
                                Email *
                              </label>
                              <input
                                type="email"
                                id="enquiry-email"
                                value={enquiryData.email}
                                onChange={(e) => setEnquiryData(prev => ({ ...prev, email: e.target.value }))}
                                required
                                maxLength={255}
                                className="w-full px-4 py-3 rounded-lg border border-input bg-background text-foreground"
                              />
                            </div>

                            <div>
                              <label htmlFor="enquiry-subject" className="block text-sm font-medium text-foreground mb-2">
                                Subject *
                              </label>
                              <input
                                type="text"
                                id="enquiry-subject"
                                value={enquiryData.subject}
                                onChange={(e) => setEnquiryData(prev => ({ ...prev, subject: e.target.value }))}
                                required
                                maxLength={200}
                                className="w-full px-4 py-3 rounded-lg border border-input bg-background text-foreground"
                              />
                            </div>

                            <div>
                              <label htmlFor="enquiry-message" className="block text-sm font-medium text-foreground mb-2">
                                Message *
                              </label>
                              <textarea
                                id="enquiry-message"
                                value={enquiryData.message}
                                onChange={(e) => setEnquiryData(prev => ({ ...prev, message: e.target.value }))}
                                required
                                rows={6}
                                maxLength={1000}
                                className="w-full px-4 py-3 rounded-lg border border-input bg-background text-foreground resize-none"
                              />
                              <p className="text-xs text-muted-foreground mt-1">
                                {enquiryData.message.length}/1000 characters
                              </p>
                            </div>

                            <Button type="submit" size="lg" className="w-full rounded-full">
                              Send Enquiry
                            </Button>
                          </form>
                        </TabsContent>

                        <TabsContent value="feedback">
                          <form onSubmit={handleFeedbackSubmit} className="space-y-6">
                            <div>
                              <label htmlFor="feedback-name" className="block text-sm font-medium text-foreground mb-2">
                                Name *
                              </label>
                              <input
                                type="text"
                                id="feedback-name"
                                value={feedbackData.name}
                                onChange={(e) => setFeedbackData(prev => ({ ...prev, name: e.target.value }))}
                                required
                                maxLength={100}
                                className="w-full px-4 py-3 rounded-lg border border-input bg-background text-foreground"
                              />
                            </div>

                            <div>
                              <label htmlFor="feedback-email" className="block text-sm font-medium text-foreground mb-2">
                                Email *
                              </label>
                              <input
                                type="email"
                                id="feedback-email"
                                value={feedbackData.email}
                                onChange={(e) => setFeedbackData(prev => ({ ...prev, email: e.target.value }))}
                                required
                                maxLength={255}
                                className="w-full px-4 py-3 rounded-lg border border-input bg-background text-foreground"
                              />
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-foreground mb-2">
                                Rating *
                              </label>
                              <div className="flex gap-2">
                                {[1, 2, 3, 4, 5].map((star) => (
                                  <button
                                    key={star}
                                    type="button"
                                    onClick={() => setFeedbackData(prev => ({ ...prev, rating: star }))}
                                    className={`h-12 w-12 rounded-lg border-2 transition-all ${
                                      feedbackData.rating >= star 
                                        ? 'bg-primary border-primary text-white' 
                                        : 'border-input hover:border-primary'
                                    }`}
                                  >
                                    {star}
                                  </button>
                                ))}
                              </div>
                            </div>

                            <div>
                              <label htmlFor="feedback-category" className="block text-sm font-medium text-foreground mb-2">
                                Category *
                              </label>
                              <select
                                id="feedback-category"
                                value={feedbackData.category}
                                onChange={(e) => setFeedbackData(prev => ({ ...prev, category: e.target.value }))}
                                required
                                className="w-full px-4 py-3 rounded-lg border border-input bg-background text-foreground"
                              >
                                <option value="">Select a category</option>
                                <option value="platform">Platform Experience</option>
                                <option value="streaming">Streaming Quality</option>
                                <option value="features">Features</option>
                                <option value="support">Customer Support</option>
                                <option value="other">Other</option>
                              </select>
                            </div>

                            <div>
                              <label htmlFor="feedback-message" className="block text-sm font-medium text-foreground mb-2">
                                Your Feedback *
                              </label>
                              <textarea
                                id="feedback-message"
                                value={feedbackData.feedback}
                                onChange={(e) => setFeedbackData(prev => ({ ...prev, feedback: e.target.value }))}
                                required
                                rows={6}
                                maxLength={1000}
                                className="w-full px-4 py-3 rounded-lg border border-input bg-background text-foreground resize-none"
                              />
                              <p className="text-xs text-muted-foreground mt-1">
                                {feedbackData.feedback.length}/1000 characters
                              </p>
                            </div>

                            <Button type="submit" size="lg" className="w-full rounded-full">
                              Submit Feedback
                            </Button>
                          </form>
                        </TabsContent>
                      </Tabs>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Quick Links */}
        <section className="py-20 bg-gradient-to-b from-background to-primary/5">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-2xl font-bold text-foreground mb-4">
                Looking for something specific?
              </h2>
              <div className="flex flex-wrap justify-center gap-4 mt-8">
                <Button variant="outline" className="rounded-full" asChild>
                  <a href="/help">Help Center</a>
                </Button>
                <Button variant="outline" className="rounded-full" asChild>
                  <a href="/blog">Blog</a>
                </Button>
                <Button variant="outline" className="rounded-full" asChild>
                  <a href="/careers">Careers</a>
                </Button>
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
};

export default Contact;
